import environment from '@department-of-veterans-affairs/platform-utilities/environment';
import footerContent from 'platform/forms/components/FormFooter';
import { addressValidationPage } from 'platform/forms-system/src/js/patterns/address-validation';
import nameAndDateOfBirth from '../pages/nameAndDateOfBirth';
import identificationInformation from '../pages/identificationInformation';
import mailingAddress from '../pages/mailingAddress';
import phoneAndEmailAddress from '../pages/phoneAndEmailAddress';
import relationshipToVeteran from '../pages/relationshipToVeteran';
import serviceBranch from '../pages/serviceBranch';
import uploadFile from '../pages/upload';
import supportingDocuments from '../pages/supportingDocuments';
import manifest from '../manifest.json';
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import { treatmentRecordsPages } from '../pages/treatmentRecords';
import { employersPages } from '../pages/employers';
import {
  supportingDocumentsArrayRequiredPages,
  supportingDocumentsArrayOptionalPages,
} from '../pages/supportingDocumentsArray';
import internationalContact from '../pages/internationalContact';
import pastApplications from '../pages/pastApplications';
import BackToIndexLink from '../components/BackToIndexLink';
import NeedHelp from '../components/NeedHelp';

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  dev: {
    showNavLinks: false,
  },
  v3SegmentedProgressBar: true,
  submitUrl: `${environment.API_URL}/simple_forms_api/v1/simple_forms`,
  submit: () =>
    Promise.resolve({ attributes: { confirmationNumber: '123123123' } }),
  trackingPrefix: 'mock-form-patterns-',
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  formId: 'FORM_MOCK_PATTERNS_V3',
  saveInProgress: {},
  version: 0,
  prefillEnabled: true,
  footerContent,
  getHelp: NeedHelp,
  savedFormMessages: {
    notFound: 'Please start over to apply for web component examples.',
    noAuth:
      'Please sign in again to continue your application for web component examples.',
  },
  title: 'Explore Pattern Demonstrations in Our Sample Form',
  preSubmitInfo: {
    statementOfTruth: {
      body: 'I confirm that the identifying information in this form is accurate and has been represented correctly.',
      fullNamePath: 'fullName',
    },
  },
  defaultDefinitions: {},
  chapters: {
    personalInformationChapter: {
      title: 'Your personal information',
      pages: {
        personalInformation1: {
          path: 'name-and-date-of-birth',
          title: 'Name and date of birth',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: nameAndDateOfBirth.uiSchema,
          schema: nameAndDateOfBirth.schema,
        },
        personalInformation2: {
          path: 'identification-information',
          title: 'Identification information',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: identificationInformation.uiSchema,
          schema: identificationInformation.schema,
        },
        relationshipToVeteran: {
          path: 'relationship-to-veteran',
          title: 'Relationship to Veteran',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: relationshipToVeteran.uiSchema,
          schema: relationshipToVeteran.schema,
        },
      },
    },
    mailingAddressChapter: {
      title: 'Mailing address',
      pages: {
        mailingAddress: {
          path: 'mailing-address',
          title: 'Mailing address',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: mailingAddress.uiSchema,
          schema: mailingAddress.schema,
        },
        // Demo of the address-validation pattern — always on in this mock
        // form (production forms gate it with the forms_address_validation
        // toggle via `depends`).
        mailingAddressValidation: addressValidationPage({
          addressPath: 'address',
          path: 'mailing-address-validation',
        }),
      },
    },
    contactInformationChapter: {
      title: 'Contact information',
      pages: {
        phoneAndEmailAddress: {
          path: 'phone-and-email-address',
          title: 'Phone and email address',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: phoneAndEmailAddress.uiSchema,
          schema: phoneAndEmailAddress.schema,
        },
      },
    },
    internationalContact: {
      title: 'International Contact Information',
      pages: {
        internationalContact: {
          path: 'international-contact',
          title: 'International contact',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: internationalContact.uiSchema,
          schema: internationalContact.schema,
        },
      },
    },
    serviceBranchChapter: {
      title: 'Service Branch',
      pages: {
        serviceBranch: {
          path: 'service-branch',
          title: 'Service Branch',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: serviceBranch.uiSchema,
          schema: serviceBranch.schema,
        },
      },
    },
    uploadFile: {
      title: 'File upload single',
      pages: {
        uploadFile: {
          path: 'upload-file',
          title: 'Upload file',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: uploadFile.uiSchema,
          schema: uploadFile.schema,
        },
      },
    },
    supportingDocuments: {
      title: 'File upload multiple',
      pages: {
        supportingDocuments: {
          path: 'supporting-documents',
          title: 'Supporting documents',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: supportingDocuments.uiSchema,
          schema: supportingDocuments.schema,
        },
      },
    },
    supportingDocumentsArrayRequired: {
      title: 'File upload multiple + additional info (required)',
      pages: supportingDocumentsArrayRequiredPages,
    },
    supportingDocumentsArrayOptional: {
      title: 'File upload multiple + additional info (optional)',
      pages: supportingDocumentsArrayOptionalPages,
    },
    pastApplications: {
      title: 'Your past applications',
      pages: {
        pastApplications: {
          path: 'past-applications',
          title: 'Past applications',
          ContentBeforeButtons: BackToIndexLink,
          uiSchema: pastApplications.uiSchema,
          schema: pastApplications.schema,
        },
      },
    },
    treatmentRecords: {
      title: 'Treatment records',
      pages: treatmentRecordsPages,
    },
    employers: {
      title: 'Your employers',
      pages: employersPages,
    },
  },
};

export default formConfig;
